import { ethers } from 'ethers'
import { getMulticallContract } from 'utils/contractHelpers'
import { MultiCallResponse } from './types'

export interface Call {
  address: string // Address of the contract
  name: string // Function name on the contract (example: balanceOf)
  params?: any[] // Function params
}

export interface CallWithAbi {
  address: string // Address of the contract
  name: string // Function name on the contract (example: balanceOf)
  params?: any[] // Function params
  abi?: any
}

interface MulticallOptions {
  requireSuccess?: boolean
}

const multicall = async <T = any>(abi: any[], calls: Call[]): Promise<T> => {
  try {
    const multi = getMulticallContract()
    const itf = new ethers.utils.Interface(abi)
    const calldata = calls.map((call) => [call.address.toLowerCase(), itf.encodeFunctionData(call.name, call.params)])
    const { returnData } = await multi.aggregate(calldata)
    const res = returnData.map((call, i) => itf.decodeFunctionResult(calls[i].name, call))

    return res
  } catch (error) {
    console.error(error)
    return null
  }
}

export const multicallWithAbi = async <T = any>(calls: CallWithAbi[]): Promise<T> => {
  try {
    const multi = getMulticallContract()
    const calldata = calls.map((call) => {
      const itf = new ethers.utils.Interface(call.abi)
      return [call.address.toLowerCase(), itf.encodeFunctionData(call.name, call.params)]
    })
    const { returnData } = await multi.aggregate(calldata)
    const res = returnData.map((call, i) => {
      const itf = new ethers.utils.Interface(calls[i].abi)
      return itf.decodeFunctionResult(calls[i].name, call)
    })

    return res
  } catch (error) {
    console.error(error)
    return null
  }
}

export const multicallWithAbiV2 = async <T = any>(
  calls: CallWithAbi[],
  options: MulticallOptions = { requireSuccess: true },
): Promise<T> => {
  try {
    const { requireSuccess } = options
    const multi = getMulticallContract()
    const calldata = calls.map((call) => {
      const itf = new ethers.utils.Interface(call.abi)
      return [call.address.toLowerCase(), itf.encodeFunctionData(call.name, call.params)]
    })
    const returnData = await multi.tryAggregate(requireSuccess, calldata)
    const res = returnData.map((call, i) => {
      const [result, data] = call
      const itf = new ethers.utils.Interface(calls[i].abi)
      return result ? itf.decodeFunctionResult(calls[i].name, data) : null
    })

    return res
  } catch (error) {
    console.error(error)
    return null
  }
}

/**
 * Multicall V2 uses the new "tryAggregate" function. It is different in 2 ways
 *
 * 1. If "requireSuccess" is false multicall will not bail out if one of the calls fails
 * 2. The return inclues a boolean whether the call was successful e.g. [wasSuccessfull, callResult]
 */
export const multicallv2 = async <T = any>(
  abi: any[],
  calls: Call[],
  options: MulticallOptions = { requireSuccess: true },
): Promise<MultiCallResponse<T>> => {
  const { requireSuccess } = options
  const multi = getMulticallContract()
  const itf = new ethers.utils.Interface(abi)

  const calldata = calls.map((call) => [call.address.toLowerCase(), itf.encodeFunctionData(call.name, call.params)])
  const returnData = await multi.tryAggregate(requireSuccess, calldata)
  const res = returnData.map((call, i) => {
    const [result, data] = call
    return result ? itf.decodeFunctionResult(calls[i].name, data) : null
  })

  return res
}

export default multicall
